import get from 'lodash/get';

import { MutationType } from '../constants';

import type {
	CardDeleteMutation,
	CardUpdateMutation,
	CardCreateMutation,
	CardMutation,
	CardMutationMeta,
	CardMutationMetaOptions,
	CardMutationFactory,
} from '../types';

const makeMutation = (type: MutationType, cardId: number): CardMutation => ({
	type,
	cardId,
});

export function makeCreateMutation(cardId: number): CardMutation {
	return makeMutation(MutationType.Create, cardId);
}

export function makeUpdateMutation(cardId: number): CardMutation {
	return makeMutation(MutationType.Update, cardId);
}

export function makeDeleteMutation(cardId: number): CardMutation {
	return makeMutation(MutationType.Delete, cardId);
}

export function isCreateMutation(mutation: CardMutation): mutation is CardCreateMutation {
	return mutation.type === MutationType.Create;
}

export function isUpdateMutation(mutation: CardMutation): mutation is CardUpdateMutation {
	return mutation.type === MutationType.Update;
}

export function isDeleteMutation(mutation: CardMutation): mutation is CardDeleteMutation {
	return mutation.type === MutationType.Delete;
}

const toCardIds = (cardIdOrCardIds: number | number[]): number[] =>
	Array.isArray(cardIdOrCardIds) ? cardIdOrCardIds : [cardIdOrCardIds];

const toCardMutations = (
	issueIdOrIssueIds: number | number[],
	mutationFactory: CardMutationFactory,
): CardMutation[] => toCardIds(issueIdOrIssueIds).map(mutationFactory);

function buildMutationMeta(mutations: CardMutation[]): CardMutationMeta {
	return {
		mutations,
	};
}

const defaultOptions: CardMutationMetaOptions = {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	createCardIds: [] as number[],
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	updateCardIds: [] as number[],
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	deleteCardIds: [] as number[],
};

export function makeMutationMeta(
	options: Partial<CardMutationMetaOptions> = defaultOptions,
): CardMutationMeta {
	const { createCardIds, updateCardIds, deleteCardIds }: CardMutationMetaOptions = {
		...defaultOptions,
		...options,
	};

	return buildMutationMeta(
		toCardMutations(createCardIds, makeCreateMutation)
			.concat(toCardMutations(updateCardIds, makeUpdateMutation))
			.concat(toCardMutations(deleteCardIds, makeDeleteMutation)),
	);
}

export function makeCreateMutationMeta(cardIdOrCardIds: number | number[]): CardMutationMeta {
	return makeMutationMeta({ createCardIds: cardIdOrCardIds });
}

export function makeUpdateMutationMeta(cardIdOrCardIds: number | number[]): CardMutationMeta {
	return makeMutationMeta({ updateCardIds: cardIdOrCardIds });
}

export function makeDeleteMutationMeta(cardIdOrCardIds: number | number[]): CardMutationMeta {
	return makeMutationMeta({ deleteCardIds: cardIdOrCardIds });
}

export function asCardMutations(entity: unknown): CardMutation[] | undefined {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return get(entity, ['meta', 'mutations']) as CardMutation[] | undefined;
}
